import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { config } from '../../config';
import { decodeNotificationsResponse } from './notificationsMapper';
import { prepareHeaders } from '../../configuration/tokenHandling/prepareHeaders';
import { handleResponse } from './responseHandler';
import type { Notification, NotificationsResponse } from './notificationsTypes';
import { markedAllNotificationsAsRead, notificationsChanged } from '../notifications/notificationSlice';

export const NOTIFICATION_API_LIMIT = 25;

// Query parameters types
type NotificationsQueryParams = {
    limit: number | undefined;
    language: string | undefined;
    entitySourceId?: string | undefined;
};

type NotificationsAfterQueryParams = NotificationsQueryParams & { after: string | undefined };

type SetBookmarkQueryParams = { userId: string; readUntil: string; entitySourceId?: string | undefined };

// Response types
type GetNotificationsResponse = {
    notifications: Notification[];
    afterTag: string | undefined;
};

type GetNotificationsAfterResponse = {
    notifications: Notification[];
    afterTag: string | undefined;
    hasNext: boolean;
};

export type BookmarkRequestBody = {
    read_until: string;
};

export const notificationsApi = createApi({
    reducerPath: 'notificationsApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.PLATFORM_NOTIFICATION_SERVICE, prepareHeaders }),
    keepUnusedDataFor: 0, // This ensures every time the entity is changed, RTKQ re-queries even if it has cached data
    endpoints: builder => ({
        // Fetch initial page of notifications
        getNotifications: builder.query<GetNotificationsResponse, NotificationsQueryParams>({
            query: ({ limit = 1000, language, entitySourceId }) => {
                const urlSearchParams = new URLSearchParams();
                urlSearchParams.set('limit', limit.toString());
                if (language) {
                    urlSearchParams.set('language', language);
                }
                if (entitySourceId) {
                    urlSearchParams.set('source_entity_type', 'ASSET');
                    urlSearchParams.set('source_entity_identifier', entitySourceId);
                }

                return {
                    url: `/notifications?${urlSearchParams}`,
                    responseHandler: response => handleResponse(response),
                };
            },
            transformResponse: (rawResult: NotificationsResponse) => decodeNotificationsResponse(rawResult),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    const isFullPage = data.notifications?.length === NOTIFICATION_API_LIMIT;
                    dispatch(
                        notificationsChanged({
                            notifications: data.notifications,
                            entitySourceId: arg.entitySourceId,
                            afterTag: data.afterTag,
                            // The API returns as much notifications as defined in the fetch limit. Assuming there are more.
                            // If there are less notifications returned by the API as defined in the fetch limit, then we
                            // know that there are no more un-fetched notifications.
                            hasNext: isFullPage,
                        })
                    );
                } catch (error) {
                    console.error(error);
                }
            },
        }),

        // Fetch more pages of notifications
        getNotificationsAfter: builder.query<GetNotificationsAfterResponse, NotificationsAfterQueryParams>({
            query: ({ limit = 1000, after, language, entitySourceId }) => {
                const urlSearchParams = new URLSearchParams();
                urlSearchParams.set('limit', limit.toString());
                if (after) {
                    urlSearchParams.set('after', after);
                }
                if (language) {
                    urlSearchParams.set('language', language);
                }
                if (entitySourceId) {
                    // TODO: instead of hardcoding ASSET as type here, we should get the proper source entity type
                    urlSearchParams.set('source_entity_type', 'ASSET');
                    urlSearchParams.set('source_entity_identifier', entitySourceId);
                }

                return {
                    url: `/notifications?${urlSearchParams}`,
                    responseHandler: async (response: Response) => handleResponse(response),
                };
            },
            transformResponse: (rawResult: NotificationsResponse, _, arg) => {
                const decodedResponse = decodeNotificationsResponse(rawResult);
                const isFullPage = decodedResponse.notifications?.length === NOTIFICATION_API_LIMIT;
                const cursorChanged = decodedResponse.afterTag !== arg.after;

                return {
                    ...decodedResponse,
                    hasNext: isFullPage && cursorChanged,
                };
            },
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        notificationsChanged({
                            notifications: data.notifications,
                            entitySourceId: arg.entitySourceId,
                            afterTag: data.afterTag,
                            hasNext: data.hasNext,
                        })
                    );
                } catch (error) {
                    console.error(error);
                }
            },
        }),

        // Update the state of a single notification
        updateNotification: builder.mutation<void, Notification>({
            query: ({ id, state }) => ({
                url: `/notifications/${id}`,
                method: 'PATCH',
                body: { state: { value: state } },
                responseHandler: async (response: Response) => handleResponse(response),
            }),
        }),

        setBookmark: builder.mutation<void, SetBookmarkQueryParams>({
            query: ({ userId, readUntil, entitySourceId }) => ({
                url: `/bookmarks/${userId}`,
                method: 'PUT',
                body: {
                    read_until: readUntil,
                    // TODO: currently only supports source entities of type ASSET
                    source_entity: entitySourceId ? { type: 'ASSET', identifier: entitySourceId } : undefined,
                },
                responseHandler: async (response: Response) => handleResponse(response),
            }),
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                    dispatch(markedAllNotificationsAsRead());
                } catch (error) {
                    console.error(error);
                }
            },
        }),
    }),
});

export const {
    useGetNotificationsQuery,
    useGetNotificationsAfterQuery,
    useLazyGetNotificationsAfterQuery,
    useUpdateNotificationMutation,
    useSetBookmarkMutation,
} = notificationsApi;
